// SEO indexing gate — host-aware.
//
// PENTING: satu build + satu .env melayani myfitsociety.com (produksi) DAN
// mfsdev.net / prod.mfsdev.net (staging). Cloudflare mengarahkan semua host ke
// app Next yang sama (gitlab-ci deploy ke /var/www/mfsdev.net). Jadi deteksi
// produksi WAJIB dari Host header, bukan dari env (API_URL sama di semua host).
//
// Pola ini mengikuti isMetaPixelHost di helpers/ads/conversion-tracking.js yang
// sudah terbukti jalan di produksi via ctx.req.headers.host.

// Apex adalah domain kanonik (lihat hasil Google: https://myfitsociety.com/...).
// www disertakan defense-in-depth: kalau Cloudflare TIDAK redirect www->apex dan
// www sampai ke app, www tetap indexable TAPI canonical-nya selalu ke apex
// (getCanonicalUrl), jadi Google konsolidasi www->apex tanpa duplicate content.
const PRODUCTION_HOSTS = ["myfitsociety.com", "www.myfitsociety.com"];

const PRODUCTION_ORIGIN = "https://myfitsociety.com";

// Path (prefix) yang TETAP noindex walau di domain produksi: halaman privat /
// app / utility / share / deeplink yang bukan halaman marketing dan tidak boleh
// jadi hasil Google. Halaman ini di-noindex via meta (tetap boleh di-crawl supaya
// Google membaca noindex-nya), kecuali yang juga ada di ROBOTS_DISALLOW.
const NOINDEX_PATH_PREFIXES = [
    "/auth",
    "/payment",
    "/profile",
    "/admin",
    "/change-email",
    "/reset-password",
    "/session-expire",
    "/unsubscribe",
    "/email-preference",
    "/forge-redeem",
    "/voucher-expire",
    "/collab-voucher",
    // /collab-voucher-limit = halaman "kuota voucher habis" (system page seperti
    // /voucher-expire), bukan marketing — tidak ikut ke-match prefix "/collab-voucher"
    // karena bentuknya "collab-voucher-limit", bukan "collab-voucher/<sub>".
    "/collab-voucher-limit",
    "/lang",
    "/under-maintenance",
    // /blank-* = halaman template/dev sisa starter — bukan konten publik.
    // Ketiganya didaftarkan eksplisit karena match-nya exact/prefix-per-segmen
    // ("/blank" tidak akan menyambar "/blank-page").
    "/blank-custom",
    "/blank-page",
    "/blank-style",
    // /subscribe/* = halaman aktivasi ber-token (pages/subscribe/activate/[id]),
    // kelas yang sama dengan /auth/activate — noindex via meta, tidak Disallow.
    "/subscribe",
    "/api",
    "/applink",
    "/shares",
    "/download-app",
    "/app-download",
    // /app/* = alias internal yang me-render /tools/* (mis. /app/bmi-calculator
    // -> /tools/bmi-calculator). Halaman kanonik = /tools/*; alias-nya noindex
    // supaya tidak duplicate content. ("/app" TIDAK match "/app-download"/"/applink").
    "/app",
    // /flashsales* = alias kampanye yang me-render /new-landing-page & /promo/quiz.
    // Halaman kanonik = target-nya; alias kampanye noindex (bukan konten SEO).
    "/flashsales",
    // Halaman utilitas / akun bisnis / koneksi wearable / konten CMS kosong /
    // share — bukan landing pencarian (Trello 430). Alias funnel (/premium,
    // /promo, /deals, /plan, dst.) sengaja TIDAK ada di sini.
    "/business",
    "/wearables",
    "/cms-landing",
    "/page/fluid-static-content",
    "/page/share-workout",
    // Langkah turunan funnel questionnaire/quiz. Halaman masuknya
    // (/questionnaire, /questionnaire-v2, /promo/quiz) tetap indexable.
    "/questionnaire/summary",
    "/questionnaire/price-login",
    "/questionnaire-v2/summary",
    "/promo/quiz/summary",
];

// Path yang BENAR-BENAR tidak boleh di-crawl (sensitif / ber-token). Hanya ini
// yang masuk Disallow robots.txt. JANGAN blokir halaman yang cuma mau di-noindex
// — kalau di-Disallow, Google tak bisa baca meta noindex dan URL-nya tetap
// nyangkut di index sebagai "no info" (ini akar masalah Disallow: / sebelumnya).
const ROBOTS_DISALLOW = [
    "/api/",
    "/admin/",
    "/payment/",
    "/reset-password/",
    "/change-email/",
];

// Agent pencarian yang mendapat grup eksplisit di robots.txt produksi.
// Classic: Googlebot + Bingbot. AI search: OAI-SearchBot (ChatGPT Search),
// PerplexityBot (Perplexity), ClaudeBot (Claude/Anthropic web search & fetch).
// Grup eksplisit membuat crawler-agent checker membaca allow langsung per-agent
// alih-alih fallback ke wildcard — dan membuat dukungan AI search engine terlihat
// jelas saat robots.txt diaudit. Token training-only (GPTBot, Google-Extended,
// CCBot) sengaja tidak diberi grup: mereka bukan search engine dan tetap tunduk
// pada wildcard `User-agent: *`.
const ROBOTS_EXPLICIT_AGENTS = [
    "Googlebot",
    "Bingbot",
    "OAI-SearchBot",
    "PerplexityBot",
    "ClaudeBot",
];

// Buang port, spasi, dan trailing dot FQDN ("myfitsociety.com." -> "myfitsociety.com").
const normalizeHostname = (host) => `${host || ""}`
    .split(":")[0]
    .trim()
    .toLowerCase()
    .replace(/\.$/, "");

// True hanya untuk domain produksi persis (myfitsociety.com / www.myfitsociety.com).
// Semua selain itu (mfsdev.net, prod.mfsdev.net, localhost, subdomain lain) => false.
const isIndexableHost = (host) => {
    const hostname = normalizeHostname(host);
    if (!hostname) {
        return false;
    }
    return PRODUCTION_HOSTS.indexOf(hostname) !== -1;
};

const getCanonicalHostRedirectUrl = (req) => {
    const host = req && req.headers && req.headers.host;
    if (normalizeHostname(host) !== "www.myfitsociety.com") {
        return "";
    }
    const rawUrl = (req && (req.originalUrl || req.url)) || "/";
    const path = `${rawUrl || "/"}`;
    return `${PRODUCTION_ORIGIN}${path.charAt(0) === "/" ? path : `/${path}`}`;
};

// Ambil host dari context Next (server) atau window (client).
const getRequestHost = (ctx) => {
    const fromReq = ctx && ctx.req && ctx.req.headers && ctx.req.headers.host;
    if (fromReq) {
        return fromReq;
    }
    if (typeof window !== "undefined" && window.location) {
        return window.location.host;
    }
    return "";
};

// Path bersih (tanpa query) dari context Next.
// PENTING: pakai req.originalUrl dulu — middleware next-i18next merewrite req.url
// dan membuang prefix locale /id, sehingga canonical /id akan salah jadi /.
// Express mempertahankan originalUrl = path asli sebelum rewrite.
const getRequestPath = (ctx) => {
    const rawUrl = (ctx && ctx.req && ctx.req.originalUrl)
        || (ctx && ctx.req && ctx.req.url)
        || (ctx && ctx.asPath)
        || (ctx && ctx.pathname)
        || "/";
    return `${rawUrl}`.split("?")[0].split("#")[0] || "/";
};

// True jika path termasuk denylist privat/app (selalu noindex).
const isNoindexPath = (path) => {
    const cleanPath = `${path || "/"}`.split("?")[0].split("#")[0];
    // Buang prefix locale subpath /id supaya cocok dengan denylist.
    const withoutLocale = cleanPath.replace(/^\/id(\/|$)/, "/");
    return NOINDEX_PATH_PREFIXES.some(
        (prefix) => withoutLocale === prefix || withoutLocale.indexOf(`${prefix}/`) === 0
    );
};

// Halaman boleh diindeks hanya jika host produksi DAN path bukan denylist.
const isIndexableForCtx = (ctx) => {
    if (!isIndexableHost(getRequestHost(ctx))) {
        return false;
    }
    return !isNoindexPath(getRequestPath(ctx));
};

// Trello 518 "WEB PROD" — beberapa fitur checkout dinonaktifkan HANYA di host
// produksi myfitsociety.com (staging/host lain tetap aktif):
//   * payment_mitigation.blocked -> swap daftar package jadi kartu store
//   * popup konfirmasi saldo e-wallet sebelum modal nama/telepon
// Satu build melayani semua host, jadi gate-nya dari Host header, bukan env.
const isMyFitsocietyProdHost = (host) => isIndexableHost(host);

// ctx = Next getInitialProps ctx (server). Client-side cukup dipanggil tanpa
// argumen — getRequestHost jatuh ke window.location.host.
const isMyFitsocietyProd = (ctx) => isMyFitsocietyProdHost(getRequestHost(ctx));

// Isi atribut content untuk <meta name="robots">.
const getRobotsMetaContent = (indexable) => (indexable
    ? "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
    : "noindex, nofollow");

// Canonical selalu menunjuk ke domain produksi (myfitsociety.com) pada path yang sama.
// Mengarahkan crawler ke versi produksi walau yang ter-crawl host lain.
const getCanonicalUrl = (ctx) => {
    const path = getRequestPath(ctx);
    if (path === "/") {
        return `${PRODUCTION_ORIGIN}/`;
    }
    return `${PRODUCTION_ORIGIN}${path.replace(/\/+$/, "")}`;
};

// og:locale per halaman: halaman Indonesia (prefix /id) = id_ID, selain itu en_US.
const getOgLocale = (lang) => (lang === "id" ? "id_ID" : "en_US");

// Meta description dari teks pratinjau: maks 160 karakter, dipotong di batas
// kata, tanpa "..." di ujung. Backend memotong content_preview di tengah kata
// lalu menambah "...", jadi kata terakhir yang terpotong dibuang.
const META_DESCRIPTION_MAX = 160;
const toMetaDescription = (text, max = META_DESCRIPTION_MAX) => {
    let value = `${text || ""}`.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    const hadEllipsis = /(\.\.\.|\u2026)$/.test(value);
    value = value.replace(/(\.\.\.|\u2026)+$/, "").trim();
    const cutAtWord = (str) => {
        const idx = str.lastIndexOf(" ");
        return idx > 0 ? str.slice(0, idx) : str;
    };
    if (value.length > max) {
        value = value.charAt(max) === " " ? value.slice(0, max) : cutAtWord(value.slice(0, max));
    } else if (hadEllipsis && !/[.!?]$/.test(value)) {
        value = cutAtWord(value);
    }
    return value.replace(/[\s,;:\-\u2013\u2014]+$/, "");
};

// Slug kategori artikel (menu artikel di components/Header/Header.js). Dipakai
// breadcrumb artikel dan sitemap.
const ARTICLE_CATEGORY_SLUGS = [
    "fatloss",
    "workouts",
    "nutrition",
    "healthy-living",
    "personal-development",
];

// JSON-LD halaman artikel: Article + BreadcrumbList. Hanya memakai data yang
// sudah tampil di halaman (judul, ringkasan, gambar, kategori); tanpa tanggal
// karena tanggal tidak tampil. Penulis = Organization Myfitsociety, publisher
// merujuk node #organization sitewide (components/head.js). Node Article
// menunjuk node WebPage per halaman (`<canonical>#webpage`) dari document.
// Kategori masuk breadcrumb hanya kalau namanya cocok dengan slug yang dikenal.
const buildArticleJsonLd = ({
    canonicalUrl,
    title,
    description,
    image,
    language,
    category,
    homeLabel,
    articlesLabel,
    categoryLabel,
}) => {
    const lang = language === "id" ? "id" : "en";
    const base = lang === "id" ? `${PRODUCTION_ORIGIN}/id` : PRODUCTION_ORIGIN;
    const article = {
        "@type": "Article",
        "@id": `${canonicalUrl}#article`,
        headline: title,
        description,
        inLanguage: lang,
        mainEntityOfPage: { "@id": `${canonicalUrl}#webpage` },
        author: { "@type": "Organization", name: "Myfitsociety", url: `${PRODUCTION_ORIGIN}/` },
        publisher: { "@id": `${PRODUCTION_ORIGIN}/#organization` },
    };
    if (image) {
        article.image = [image];
    }
    if (category) {
        article.articleSection = category;
    }
    const categorySlug = `${category || ""}`.trim().toLowerCase().replace(/\s+/g, "-");
    const crumbs = [
        { name: homeLabel || "Home", item: lang === "id" ? base : `${base}/` },
        { name: articlesLabel || "Articles", item: `${base}/articles` },
    ];
    if (ARTICLE_CATEGORY_SLUGS.indexOf(categorySlug) !== -1) {
        crumbs.push({
            name: categoryLabel || category,
            item: `${base}/articles/categories/${categorySlug}`,
        });
    }
    crumbs.push({ name: title, item: canonicalUrl });
    const breadcrumb = {
        "@type": "BreadcrumbList",
        "@id": `${canonicalUrl}#breadcrumb`,
        itemListElement: crumbs.map((crumb, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: crumb.name,
            item: crumb.item,
        })),
    };
    // "<" escaped: the canonical url comes from the request path and the
    // string is inlined in a <script> tag.
    return JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [article, breadcrumb],
    }).replace(/</g, "\\u003c");
};

// Konten robots.txt host-aware. Produksi: izinkan + sitemap + grup eksplisit
// untuk agent pencarian classic & AI. Selain itu: blokir total.
const buildRobotsTxt = (host) => {
    if (isIndexableHost(host)) {
        const agentGroups = ROBOTS_EXPLICIT_AGENTS.map((agent) => [
            `User-agent: ${agent}`,
            "Allow: /",
            ...ROBOTS_DISALLOW.map((p) => `Disallow: ${p}`),
            "",
        ].join("\n")).join("\n");
        return [
            "User-agent: *",
            "Allow: /",
            "",
            "# Hanya path sensitif/ber-token yang diblokir crawl.",
            "# Halaman app lain di-noindex via meta (tetap crawlable).",
            ...ROBOTS_DISALLOW.map((p) => `Disallow: ${p}`),
            "",
            "# Grup eksplisit: classic + AI search engine agents.",
            agentGroups,
            `Sitemap: ${PRODUCTION_ORIGIN}/sitemap.xml`,
            "",
        ].join("\n");
    }
    // Staging / non-produksi: blokir semua mesin pencari.
    return [
        "# Non-production host — block all indexing",
        "User-agent: *",
        "Disallow: /",
        "",
    ].join("\n");
};

// URL alternatif per-locale untuk hreflang. Situs punya 2 locale: EN = path
// tanpa prefix, ID = prefix /id (next-i18next localeSubpaths). Dipakai untuk
// <link rel="alternate"> di <head> dan xhtml:link di sitemap.xml.
const getAlternateLanguageUrls = (path) => {
    const cleanPath = `${path || "/"}`.split("?")[0].split("#")[0] || "/";
    const isIdPath = /^\/id(\/|$)/.test(cleanPath);
    const enPath = isIdPath
        ? (cleanPath.replace(/^\/id(\/|$)/, "/") || "/")
        : cleanPath;
    const idPath = isIdPath
        ? cleanPath
        : `/id${cleanPath === "/" ? "" : cleanPath}`;
    return {
        en: `${PRODUCTION_ORIGIN}${enPath === "/" ? "/" : enPath.replace(/\/+$/, "")}`,
        id: `${PRODUCTION_ORIGIN}${idPath.replace(/\/+$/, "")}`,
    };
};

module.exports = {
    PRODUCTION_HOSTS,
    PRODUCTION_ORIGIN,
    NOINDEX_PATH_PREFIXES,
    ROBOTS_DISALLOW,
    ROBOTS_EXPLICIT_AGENTS,
    isIndexableHost,
    isMyFitsocietyProdHost,
    isMyFitsocietyProd,
    getCanonicalHostRedirectUrl,
    getRequestHost,
    getRequestPath,
    isNoindexPath,
    isIndexableForCtx,
    getRobotsMetaContent,
    getCanonicalUrl,
    getOgLocale,
    toMetaDescription,
    ARTICLE_CATEGORY_SLUGS,
    buildArticleJsonLd,
    buildRobotsTxt,
    getAlternateLanguageUrls,
};
